import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    Box,
    Breadcrumbs,
    Card,
    CardContent,
    CardMedia,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Link,
    OutlinedInput,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import {
    Add,
    CancelTwoTone,
    CheckCircleTwoTone,
    Close,
    CreateNewFolderTwoTone,
    Delete,
    FileUpload,
    HourglassEmptyTwoTone,
    Search,
} from '@mui/icons-material';
import { blue } from '@mui/material/colors';
import { SCUIButton } from '../../../../atomic/atoms';
import SCUITreeView from '../SCUITreeView/SCUITreeView';
import { sampleFolder } from '../../../../assets/images';
import { StepWebsiteAppearenceServiceComponent } from '../../services';
import { showSnackbar } from '../../../../stores/redux/actions';
import { enumSnackbarStatusType } from '../../../../types/enums';
import { BadgeButtonProps, ICampaignBadge } from '../../types/interfaces/CampaingTypes';

/** Extended image data returned from onSelect callback */
export interface SelectedImageData {
    id: number;
    fileName: string;
    assetUrl: string;
    /** Culture/language code of the badge being edited */
    culture?: string;
    /** Asset target (Web, MobileWeb, MobileApp) */
    assetTarget?: string;
}

export interface SCUIImageGalleryProps {
    /** Whether the modal is open */
    open: boolean;
    /** Callback when the modal is closed */
    onClose: () => void;
    /** Callback when an image is selected - receives the selected image URL and extended image data */
    onSelect: (imageUrl: string, imageData?: SelectedImageData) => void;
    /** Campaign props needed for the helper service */
    campaignProps: any;
    /** Optional: Initial badge target for pre-configuration */
    initialBadgeTarget?: {
        culture: string;
        assetTarget: string;
    };
    /** Optional: Custom modal title */
    title?: string;
    /** Optional: Maximum width of the dialog (default: 'lg') */
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Optional: Badge buttons state for managed mode - when provided, onBadgeButtonsChange will be called */
    badgeButtons?: BadgeButtonProps[];
    /** Optional: Callback when badge buttons are updated (for managed mode) */
    onBadgeButtonsChange?: (badgeButtons: BadgeButtonProps[]) => void;
    /** Optional: Callback to update campaign detail badges */
    onCampaignBadgesChange?: (badges: ICampaignBadge[]) => void;
    /** Optional: Whether to show validation snackbar messages (default: true) */
    showValidationMessages?: boolean;
}

/**
 * Utility function to update badge button source
 * Matches the logic from StepWebsiteAppearenceServiceComponent.setButtonSelectionImage
 */
export const updateBadgeButtonSource = (
    badgeButtons: BadgeButtonProps[],
    culture: string,
    assetTarget: string,
    imageUrl: string
): BadgeButtonProps[] => {
    const updatedBadgeButtons = badgeButtons.map((badgeButton) => {
        if (badgeButton.culture === culture) {
            return {
                ...badgeButton,
                badges: badgeButton.badges.map((badge) => {
                    if (badge.assetTarget === assetTarget) {
                        return { ...badge, source: imageUrl };
                    }
                    return badge;
                }),
            };
        }
        return badgeButton;
    });
    return updatedBadgeButtons;
};

/**
 * Utility function to convert badge buttons to campaign badges format
 * Matches the logic from StepWebsiteAppearenceServiceComponent.setCampaignDetailForBadge
 */
export const convertBadgeButtonsToCampaignBadges = (badgeButtons: BadgeButtonProps[]): ICampaignBadge[] => {
    const assetTarget = {
        web: 'Web',
        mobileWeb: 'MobileWeb',
        mobileApp: 'MobileApp',
    };

    const campaignBadges: ICampaignBadge[] = [];

    badgeButtons.forEach((badgeButton) => {
        const hasSelectedBadge = badgeButton.badges.some(
            (badge) => (badge.source && badge.source.length > 0) || (badge.badgeName && badge.badgeName.length > 0)
        );
        if (!hasSelectedBadge) return;

        let web = '';
        let mobileWeb = '';
        let mobileApp = '';
        let badgeWebName = '';
        let badgeMobileWebName = '';
        let badgeMobileAppName = '';

        badgeButton.badges.forEach((badge) => {
            switch (badge.assetTarget) {
                case assetTarget.web:
                    web = badge.source;
                    badgeWebName = badge.badgeName ?? '';
                    break;
                case assetTarget.mobileWeb:
                    mobileWeb = badge.source;
                    badgeMobileWebName = badge.badgeName ?? '';
                    break;
                case assetTarget.mobileApp:
                    mobileApp = badge.source;
                    badgeMobileAppName = badge.badgeName ?? '';
                    break;
            }
        });

        campaignBadges.push({
            culture: badgeButton.culture,
            web,
            mobileWeb,
            mobileApp,
            badgeWebName,
            badgeMobileWebName,
            badgeMobileAppName,
        });
    });

    return campaignBadges;
};

/**
 * Utility function to delete a badge from badge buttons
 * Matches the logic from StepWebsiteAppearenceServiceComponent.deleteCampaignBadge
 */
export const deleteBadgeFromButtons = (
    badgeButtons: BadgeButtonProps[],
    culture: string,
    assetTarget: string
): BadgeButtonProps[] => {
    return updateBadgeButtonSource(badgeButtons, culture, assetTarget, '');
};

const galleryCheckboxSx = { position: 'absolute', top: '8px', right: '8px' } as const;

/**
 * SCUIImageGallery - A reusable image gallery modal component for selecting badge images
 *
 * Features:
 * - Browse and search images from the badge gallery
 * - Navigate through folders using tree view
 * - Upload new images
 * - Create new folders
 * - Select single image and return URL via onSelect callback
 * - Double-click to quickly select and close
 * - Optional managed mode with badge buttons state
 * - Utility functions for badge data transformation
 */
export const SCUIImageGallery: FC<SCUIImageGalleryProps> = ({
    open,
    onClose,
    onSelect,
    campaignProps,
    initialBadgeTarget,
    title,
    maxWidth = 'lg',
    badgeButtons,
    onBadgeButtonsChange,
    onCampaignBadgesChange,
    showValidationMessages = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Local loading state to prevent global showLoading/hideLoading from causing parent re-renders
    // which would unmount/remount EarningModal and reset its internal state (activeTab, gallery open)
    const [localLoading, setLocalLoading] = useState(false);
    const localLoadingCount = useRef(0);

    // Override campaignService's showLoading/hideLoading with local versions
    const isolatedCampaignProps = useMemo(() => {
        if (!campaignProps?.campaignService) return campaignProps;
        return {
            ...campaignProps,
            campaignService: {
                ...campaignProps.campaignService,
                showLoading: () => {
                    localLoadingCount.current += 1;
                    setLocalLoading(true);
                },
                hideLoading: () => {
                    localLoadingCount.current -= 1;
                    if (localLoadingCount.current <= 0) {
                        localLoadingCount.current = 0;
                        setLocalLoading(false);
                    }
                },
            },
        };
    }, [campaignProps]);

    // Get the helper for badge management with isolated loading
    const badgeHelper = StepWebsiteAppearenceServiceComponent({ ...isolatedCampaignProps });

    // Track if we've already initialized to prevent re-initialization on parent re-renders
    const hasInitialized = useRef(false);
    const prevInitialBadgeTarget = useRef<{ culture: string; assetTarget: string } | null>(null);

    // Initialize badge gallery when modal opens - call openModal to trigger data fetch
    useEffect(() => {
        if (open) {
            // Check if we need to initialize - either first open or badge target actually changed
            const badgeTargetChanged =
                !prevInitialBadgeTarget.current ||
                prevInitialBadgeTarget.current.culture !== initialBadgeTarget?.culture ||
                prevInitialBadgeTarget.current.assetTarget !== initialBadgeTarget?.assetTarget;

            if (!hasInitialized.current || badgeTargetChanged) {
                console.log('SCUIImageGallery - useEffect (open=true, initializing):', {
                    initialBadgeTarget,
                    hasInitialized: hasInitialized.current,
                    badgeTargetChanged,
                });
                // This triggers the internal getBadges() call via the helper's useEffect
                badgeHelper.openModal();
                // Always set selected badge button to ensure the getBadges effect runs
                // The helper's useEffect depends on [isModalOpen && selectedBadgeButton]
                const badgeTargetToSet = initialBadgeTarget
                    ? {
                          culture: initialBadgeTarget.culture,
                          assetTarget: initialBadgeTarget.assetTarget,
                      }
                    : { culture: '', assetTarget: '' };
                console.log('SCUIImageGallery - Setting selectedBadgeButton:', badgeTargetToSet);
                badgeHelper.setSelectedBadgeButton(badgeTargetToSet);

                hasInitialized.current = true;
                prevInitialBadgeTarget.current = initialBadgeTarget
                    ? { culture: initialBadgeTarget.culture, assetTarget: initialBadgeTarget.assetTarget }
                    : null;
            }
        } else {
            // Reset initialization tracking when modal closes
            hasInitialized.current = false;
            prevInitialBadgeTarget.current = null;
        }
    }, [open, initialBadgeTarget?.culture, initialBadgeTarget?.assetTarget]);

    // Separate component for new folder input to prevent re-rendering on every keystroke
    const NewFolderInput = ({
        initialValue,
        onKeyDownSave,
        label,
        placeholder,
    }: {
        initialValue: string;
        onKeyDownSave: (event: React.KeyboardEvent<HTMLInputElement>) => void;
        label: string;
        placeholder: string;
    }) => {
        const [localValue, setLocalValue] = useState(initialValue);

        // Sync with initial value when it changes (e.g., when modal reopens)
        useEffect(() => {
            setLocalValue(initialValue);
        }, [initialValue]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && localValue.trim()) {
                // Create a synthetic event object that matches what saveNewFolder expects
                const syntheticEvent = {
                    ...e,
                    key: 'Enter',
                    target: {
                        ...e.target,
                        value: localValue.trim(),
                    },
                };
                onKeyDownSave(syntheticEvent as unknown as React.KeyboardEvent<HTMLInputElement>);
            }
        };

        return (
            <TextField
                id="folderName"
                name="folderName"
                label={label}
                placeholder={placeholder}
                type="text"
                size="small"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
        );
    };

    const GalleryDialog = styled(Dialog)(({ theme }) => ({
        '& .MuiDialogContent-root': {
            padding: theme.spacing(0),
        },
        '& .MuiDialogActions-root': {
            padding: theme.spacing(0),
        },
        '& .MuiGrid-item': {
            paddingTop: `${theme.spacing(0)}!important`,
        },
        '& .add-image': {
            marginTop: '10px',
            marginBottom: '10px',
            width: '100%',
        },
        '& .left-panel': {
            backgroundColor: '#F6F6F6',
            height: '100%',
        },
        '& .right-panel': {},
        '& .main-folder': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '8px',
            marginBottom: '8px',
        },
        '& .badge-list': {
            minHeight: '530px',
        },
        '& .selectable-tools': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            margin: '8px 0',
        },
        '& .selectable-checkbox': {
            display: 'flex',
            justifyContent: 'space-between',
            width: '38%',
            flexDirection: 'row',
            alignItems: 'center',
        },
        '& .new-files': {
            marginTop: '16px',
            border: '1px dashed #bdbdbd',
            borderRadius: '5px',
        },
    }));

    const GalleryItem = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const isSelectedImage = (imageId: number) => {
        return badgeHelper.selectedImages.some((x: number) => x == imageId);
    };

    // Local close handler that also cleans up helper state
    const handleCloseGallery = () => {
        badgeHelper.closeModal();
        onClose();
    };

    /**
     * Process the selected image and update badge data
     * This matches the logic from StepWebsiteAppearenceServiceComponent.setButtonSelectionImage
     */
    const processImageSelection = (selectedBadge: any) => {
        // Use badgeHelper.selectedBadgeButton as primary source (set when modal opens),
        // fall back to initialBadgeTarget prop
        const culture = badgeHelper.selectedBadgeButton?.culture || initialBadgeTarget?.culture;
        const assetTarget = badgeHelper.selectedBadgeButton?.assetTarget || initialBadgeTarget?.assetTarget;

        const imageData: SelectedImageData = {
            id: selectedBadge.id,
            fileName: selectedBadge.fileName,
            assetUrl: selectedBadge.assetUrl,
            culture: culture,
            assetTarget: assetTarget,
        };

        console.log('SCUIImageGallery - processImageSelection:', {
            selectedBadge,
            culture,
            assetTarget,
            imageData,
        });

        // Call the basic onSelect callback
        onSelect(selectedBadge.assetUrl, imageData);

        // If badge buttons are provided, update them (managed mode)
        if (badgeButtons && culture && assetTarget && onBadgeButtonsChange) {
            const updatedBadgeButtons = updateBadgeButtonSource(
                badgeButtons,
                culture,
                assetTarget,
                selectedBadge.assetUrl
            );
            onBadgeButtonsChange(updatedBadgeButtons);

            // Also update campaign badges if callback is provided
            if (onCampaignBadgesChange) {
                const campaignBadges = convertBadgeButtonsToCampaignBadges(updatedBadgeButtons);
                onCampaignBadgesChange(campaignBadges);
            }
        }
    };

    // Custom save that calls onSelect with the selected image URL and data
    // Includes validation logic from StepWebsiteAppearenceServiceComponent.saveModal
    const handleSaveSelection = () => {
        if (badgeHelper.selectedImages.length === 0) {
            if (showValidationMessages) {
                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.websiteAppearance.modal.message.selectImage'),
                        status: enumSnackbarStatusType.danger,
                    })
                );
            }
            return;
        }
        if (badgeHelper.selectedImages.length > 1) {
            if (showValidationMessages) {
                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.websiteAppearance.modal.message.selectOnlyOneImage'),
                        status: enumSnackbarStatusType.danger,
                    })
                );
            }
            return;
        }

        const selectedImageId = badgeHelper.selectedImages[0];
        // Look in searchResults first (what's displayed), then fall back to badges
        // Use loose equality (==) to handle type mismatches between string and number IDs
        const selectedBadge =
            badgeHelper.searchResults?.assets?.find((x: any) => x.id == selectedImageId) ||
            badgeHelper.badges?.badgeAssets?.find((x: any) => x.id == selectedImageId);

        console.log('SCUIImageGallery - handleSaveSelection:', {
            selectedImageId,
            selectedBadge,
            searchResultsAssets: badgeHelper.searchResults?.assets?.length,
            badgeAssets: badgeHelper.badges?.badgeAssets?.length,
        });

        if (selectedBadge) {
            processImageSelection(selectedBadge);
        } else {
            console.warn('SCUIImageGallery - Could not find selected badge with ID:', selectedImageId);
        }

        handleCloseGallery();
    };

    // Custom double click that selects and saves
    // Matches the logic from StepWebsiteAppearenceServiceComponent.doubleClickImageSelection
    const handleDoubleClickSelect = (imageId: number) => {
        // Toggle selection first
        badgeHelper.toggleImageSelection(imageId);

        // Look in searchResults first (what's displayed), then fall back to badges
        // Use loose equality (==) to handle type mismatches between string and number IDs
        const selectedBadge =
            badgeHelper.searchResults?.assets?.find((x: any) => x.id == imageId) ||
            badgeHelper.badges?.badgeAssets?.find((x: any) => x.id == imageId);

        console.log('SCUIImageGallery - handleDoubleClickSelect:', {
            imageId,
            selectedBadge,
            searchResultsAssets: badgeHelper.searchResults?.assets?.length,
            badgeAssets: badgeHelper.badges?.badgeAssets?.length,
        });

        if (selectedBadge) {
            processImageSelection(selectedBadge);
        } else {
            console.warn('SCUIImageGallery - Could not find selected badge with ID:', imageId);
        }

        handleCloseGallery();
    };

    return (
        <GalleryDialog
            onClose={handleCloseGallery}
            aria-labelledby="image-galleries"
            open={open}
            maxWidth={maxWidth}
            fullWidth
        >
            <DialogTitle sx={{ m: 2, p: 2 }} id="customized-dialog-title" style={{ border: 0 }}>
                <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                    {title || t('campaignManagement.save.step.websiteAppearance.modal.title')}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseGallery}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Close />
            </IconButton>
            <DialogContent dividers>
                <Grid container>
                    <Grid
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <CardContent className="left-panel">
                            <SCUIButton
                                text={t('campaignManagement.save.step.websiteAppearance.modal.addImage')}
                                type="primary"
                                sizeType="fixed"
                                size="s"
                                startIcon={<Add style={{ color: 'white' }} />}
                                showStartIcon
                                className="add-image"
                                onClick={badgeHelper.handleAddImageClick}
                            />

                            <Input
                                type="file"
                                style={{ display: 'none' }}
                                onChange={badgeHelper.handleFileChange}
                                inputProps={{ accept: 'image/*', multiple: true }}
                                inputRef={badgeHelper.fileInputRef}
                            />

                            <Typography style={{ margin: '16px 0' }}>
                                {t('campaignManagement.save.step.websiteAppearance.modal.folders')}
                            </Typography>

                            <SCUITreeView
                                folders={badgeHelper.nestedFolders}
                                onNodeSelect={badgeHelper.handleNodeSelect}
                                defaultExpanded={badgeHelper.selectedTreeViewItem?.parents?.map((p: any) => p.id)}
                                selected={badgeHelper.selectedTreeViewItem?.id}
                            />
                        </CardContent>
                    </Grid>
                    <Grid
                        size={{
                            xs: 9,
                            sm: 9,
                            md: 9,
                            lg: 9,
                        }}
                    >
                        <CardContent className="right-panel">
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="search-input">
                                    {t('campaignManagement.save.step.websiteAppearance.modal.searchImage')}
                                </InputLabel>
                                <OutlinedInput
                                    id="search-input"
                                    type="text"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <Tooltip
                                                title={t(
                                                    'campaignManagement.save.step.websiteAppearance.modal.searchButton'
                                                )}
                                            >
                                                <IconButton
                                                    onClick={(event) => badgeHelper.handleSearch(event)}
                                                    edge="end"
                                                    style={{ margin: '0' }}
                                                    size="large"
                                                >
                                                    <Search fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    }
                                    label={t('campaignManagement.save.step.websiteAppearance.modal.searchImage')}
                                    defaultValue={badgeHelper.searchTerm}
                                    inputRef={badgeHelper.searchTermRef}
                                />
                            </FormControl>

                            {/* New Files Upload Section */}
                            <div
                                className="new-files"
                                style={{
                                    display: badgeHelper.fileTable.length > 0 ? 'block' : 'none',
                                }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Paper sx={{ width: '100%' }}>
                                        <Toolbar>
                                            <Typography
                                                sx={{ flex: '1 1 100%' }}
                                                variant="h6"
                                                id="tableTitle"
                                                component="div"
                                            >
                                                {t(
                                                    'campaignManagement.save.step.websiteAppearance.modal.newImage.title'
                                                )}
                                            </Typography>
                                            <Tooltip
                                                title={t(
                                                    'campaignManagement.save.step.websiteAppearance.modal.newImage.action.uploadButton'
                                                )}
                                            >
                                                <IconButton
                                                    onClick={() => badgeHelper.handleUpload()}
                                                    size="large"
                                                    disabled={
                                                        localLoading ||
                                                        !badgeHelper.selectedFiles.some((x: any) => x.status == 0)
                                                    }
                                                >
                                                    <FileUpload fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={t(
                                                    'campaignManagement.save.step.websiteAppearance.modal.newImage.action.closeButton'
                                                )}
                                            >
                                                <IconButton
                                                    onClick={badgeHelper.handleCloseNewFolder}
                                                    size="large"
                                                    disabled={localLoading}
                                                >
                                                    <Close fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </Toolbar>
                                        <TableContainer
                                            style={{
                                                overflow: 'auto',
                                                maxHeight: '243px',
                                                display: 'flex',
                                            }}
                                        >
                                            <Table>
                                                <TableHead>
                                                    <TableCell component="th">
                                                        {t(
                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.column.id'
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th">
                                                        {t(
                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.column.name'
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th">
                                                        {t(
                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.column.type'
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th">
                                                        {t(
                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.column.file'
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th">
                                                        {t(
                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.column.status'
                                                        )}
                                                    </TableCell>
                                                </TableHead>
                                                <TableBody>
                                                    {badgeHelper.fileTable.length > 0 &&
                                                        badgeHelper.fileTable.map(
                                                            (selectedFile: any, index: number) => (
                                                                <TableRow key={index}>
                                                                    <TableCell component="td">
                                                                        {selectedFile.id}
                                                                    </TableCell>
                                                                    <TableCell component="td">
                                                                        {selectedFile.name}
                                                                    </TableCell>
                                                                    <TableCell component="td">
                                                                        {selectedFile.type}
                                                                    </TableCell>
                                                                    <TableCell component="td">
                                                                        <img
                                                                            src={`data:${selectedFile.type};base64,${selectedFile.base64}`}
                                                                            style={{
                                                                                height: '32px',
                                                                                border: '1px solid #e0e0e0',
                                                                            }}
                                                                            alt={selectedFile.name}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell component="td">
                                                                        <Box sx={{ position: 'relative' }}>
                                                                            {!localLoading &&
                                                                                selectedFile.status == 0 && (
                                                                                    <Tooltip
                                                                                        title={t(
                                                                                            'campaignManagement.save.step.websiteAppearance.modal.newImage.status.waiting'
                                                                                        )}
                                                                                    >
                                                                                        <HourglassEmptyTwoTone
                                                                                            style={{
                                                                                                fontSize: '24px',
                                                                                                color: 'grey',
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                )}
                                                                            {selectedFile.status == 1 && (
                                                                                <Tooltip title={selectedFile.message}>
                                                                                    <CancelTwoTone
                                                                                        style={{
                                                                                            fontSize: '24px',
                                                                                            color: 'red',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            )}
                                                                            {selectedFile.status == 2 && (
                                                                                <Tooltip
                                                                                    title={t(
                                                                                        'campaignManagement.save.step.websiteAppearance.modal.newImage.status.loaded'
                                                                                    )}
                                                                                >
                                                                                    <CheckCircleTwoTone
                                                                                        style={{
                                                                                            fontSize: '24px',
                                                                                            color: 'green',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            )}
                                                                            {localLoading &&
                                                                                selectedFile.status == 0 && (
                                                                                    <CircularProgress
                                                                                        size={24}
                                                                                        sx={{
                                                                                            color: blue[500],
                                                                                            position: 'absolute',
                                                                                            top: -10,
                                                                                            left: 10,
                                                                                            zIndex: 1,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Box>
                            </div>

                            {/* Breadcrumbs and folder navigation */}
                            <div className="main-folder">
                                <Breadcrumbs aria-label="breadcrumb">
                                    <Link
                                        underline="hover"
                                        color="inherit"
                                        onClick={() => {
                                            badgeHelper.filterBadgeList();
                                            badgeHelper.setSelectedTreeViewItem({});
                                            if (badgeHelper.selectedTreeViewItem?.id != undefined) {
                                                badgeHelper.handleCloseNewFolder();
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {t('campaignManagement.save.step.websiteAppearance.modal.mainFolder')}
                                    </Link>
                                    {badgeHelper.selectedTreeViewItem &&
                                        badgeHelper.selectedTreeViewItem.parents?.map((p: any) => (
                                            <Link
                                                key={p.id}
                                                underline="hover"
                                                color="inherit"
                                                style={{ cursor: 'pointer' }}
                                                onClick={(event) => {
                                                    badgeHelper.handleNodeSelect(event, p.id);
                                                    badgeHelper.filterBadgeList(p.id);
                                                }}
                                            >
                                                {p.name}
                                            </Link>
                                        ))}
                                    {badgeHelper.selectedTreeViewItem && (
                                        <Typography color="text.primary">
                                            {badgeHelper.selectedTreeViewItem.name}
                                        </Typography>
                                    )}
                                </Breadcrumbs>
                                <IconButton
                                    aria-label="add-folder"
                                    size="large"
                                    onClick={badgeHelper.handleRequestNewFolder}
                                    disabled={badgeHelper.newFolder.isRequest}
                                >
                                    <CreateNewFolderTwoTone fontSize="inherit" />
                                </IconButton>
                            </div>

                            {/* Badge/Asset List */}
                            <div className="badge-list">
                                <div
                                    className="selectable-tools"
                                    style={{
                                        visibility:
                                            badgeHelper.searchResults.folders?.length > 0 ||
                                            badgeHelper.searchResults.assets?.length > 0
                                                ? 'visible'
                                                : 'hidden',
                                    }}
                                >
                                    <div className="selectable-checkbox">
                                        <FormGroup>
                                            <FormControlLabel
                                                control={<Checkbox />}
                                                label={
                                                    badgeHelper.selectAll
                                                        ? t(
                                                              'campaignManagement.save.step.websiteAppearance.modal.removeAll'
                                                          )
                                                        : t(
                                                              'campaignManagement.save.step.websiteAppearance.modal.selectAll'
                                                          )
                                                }
                                                checked={badgeHelper.selectAll}
                                                disabled
                                            />
                                        </FormGroup>
                                    </div>
                                    <IconButton aria-label="delete" size="large" disabled>
                                        <Delete fontSize="inherit" />
                                    </IconButton>
                                </div>

                                <Grid
                                    container
                                    spacing={2}
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    flexWrap="wrap"
                                >
                                    {/* New Folder Input */}
                                    {badgeHelper.newFolder.isRequest && (
                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 4,
                                                lg: 3,
                                            }}
                                        >
                                            <Card style={{ margin: '8px 0', position: 'relative' }}>
                                                <IconButton
                                                    onClick={() => {
                                                        badgeHelper.setNewFolder({
                                                            ...badgeHelper.newFolder,
                                                            name: '',
                                                            isRequest: false,
                                                        });
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 8,
                                                        top: 8,
                                                        color: (theme) => theme.palette.grey[500],
                                                    }}
                                                >
                                                    <Close />
                                                </IconButton>
                                                <CardMedia
                                                    component="img"
                                                    height="170"
                                                    image={sampleFolder}
                                                    style={{
                                                        padding: '10px',
                                                        backgroundSize: 'auto',
                                                        width: '100%',
                                                        objectFit: 'scale-down',
                                                    }}
                                                />
                                                <CardContent style={{ padding: '8px', textAlign: 'center' }}>
                                                    <FormControl fullWidth>
                                                        <NewFolderInput
                                                            initialValue={badgeHelper.newFolder.name}
                                                            onKeyDownSave={badgeHelper.saveNewFolder}
                                                            label={t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.folderName'
                                                            )}
                                                            placeholder={t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.pressEnter'
                                                            )}
                                                        />
                                                    </FormControl>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {/* Folders */}
                                    {badgeHelper.searchResults &&
                                        badgeHelper.searchResults.folders?.map((item: any) => (
                                            <Grid
                                                key={item.id}
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: 4,
                                                    lg: 3,
                                                }}
                                            >
                                                <Card style={{ margin: '8px 0', position: 'relative' }}>
                                                    <CardMedia
                                                        component="img"
                                                        height="170"
                                                        image={sampleFolder}
                                                        style={{
                                                            padding: '10px',
                                                            backgroundSize: 'auto',
                                                            width: '100%',
                                                            objectFit: 'scale-down',
                                                        }}
                                                        onDoubleClick={(event: any) =>
                                                            badgeHelper.handleNodeSelect(event, item.id)
                                                        }
                                                    />
                                                    <CardContent style={{ padding: '8px', textAlign: 'center' }}>
                                                        <Tooltip title={item.name}>
                                                            <Typography gutterBottom variant="h5" component="div">
                                                                {item.name?.length <= 20
                                                                    ? item.name
                                                                    : `${item.name.substring(0, 20)}...`}
                                                            </Typography>
                                                        </Tooltip>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}

                                    {/* Assets/Images */}
                                    {badgeHelper.searchResults &&
                                        badgeHelper.searchResults.assets?.map((item: any) => (
                                            <Grid
                                                key={item.id}
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: 4,
                                                    lg: 3,
                                                }}
                                            >
                                                <Card style={{ margin: '8px 0', position: 'relative' }}>
                                                    <Checkbox
                                                        sx={galleryCheckboxSx}
                                                        checked={badgeHelper.selectedImages.includes(item.id)}
                                                        onChange={() => badgeHelper.toggleImageSelection(item.id)}
                                                    />
                                                    <CardMedia
                                                        component="img"
                                                        height="170"
                                                        image={item.assetUrl}
                                                        style={{
                                                            border: isSelectedImage(item.id)
                                                                ? '2px solid #0d6efd'
                                                                : '1px solid #c8c8c8',
                                                            borderBottom: isSelectedImage(item.id)
                                                                ? ''
                                                                : '1px solid #c8c8c8',
                                                            borderRadius: '5px 5px 0 0',
                                                            padding: '10px',
                                                            backgroundSize: 'auto',
                                                            width: '100%',
                                                            objectFit: 'scale-down',
                                                            backgroundColor: '#f6f6f6',
                                                        }}
                                                        onDoubleClick={() => handleDoubleClickSelect(item.id)}
                                                    />
                                                    <CardContent
                                                        style={{
                                                            borderBottom: isSelectedImage(item.id)
                                                                ? '2px solid #0d6efd'
                                                                : '1px solid #c8c8c8',
                                                            borderLeft: isSelectedImage(item.id)
                                                                ? '2px solid #0d6efd'
                                                                : '1px solid #c8c8c8',
                                                            borderRight: isSelectedImage(item.id)
                                                                ? '2px solid #0d6efd'
                                                                : '1px solid #c8c8c8',
                                                            borderRadius: '0 0 5px 5px',
                                                            padding: '8px',
                                                        }}
                                                        onDoubleClick={() => handleDoubleClickSelect(item.id)}
                                                    >
                                                        <Tooltip title={item.fileName}>
                                                            <Typography gutterBottom variant="h5" component="div">
                                                                {item.fileName.length <= 20
                                                                    ? item.fileName
                                                                    : `${item.fileName.substring(0, 20)}...`}
                                                            </Typography>
                                                        </Tooltip>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                </Grid>
                            </div>
                        </CardContent>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Stack direction="row">
                    <GalleryItem style={{ padding: '16px 10px 16px 16px' }}>
                        <SCUIButton
                            text={t('campaignManagement.save.step.websiteAppearance.modal.action.cancelButton')}
                            type="secondary"
                            sizeType="icon"
                            size="s"
                            onClick={handleCloseGallery}
                        />
                    </GalleryItem>
                    <GalleryItem style={{ padding: '16px 16px 16px 10px' }}>
                        <SCUIButton
                            autoFocusButton
                            text={t('campaignManagement.save.step.websiteAppearance.modal.action.saveButton')}
                            type="primary"
                            sizeType="fixed"
                            size="s"
                            onClick={handleSaveSelection}
                        />
                    </GalleryItem>
                </Stack>
            </DialogActions>
        </GalleryDialog>
    );
};

export default SCUIImageGallery;

// Re-export types for convenience
export type { BadgeButtonProps, BadgeListItemProps, ICampaignBadge } from '../../types/interfaces/CampaingTypes';
